import { useState } from "react";
import { Check, MapPin, RotateCcw, X } from "lucide-react";

const ZOOM = 2.6;

function Pin({ x, y, confirmed }) {
  return (
    <div
      data-testid="map-marker"
      className="absolute pointer-events-none"
      style={{ left: `${x * 100}%`, top: `${y * 100}%`, transform: "translate(-50%, -100%)" }}
    >
      <MapPin
        size={24}
        strokeWidth={1.4}
        stroke="#ffffff"
        fill={confirmed ? "#4a7c59" : "#c97b4a"}
        style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.35))" }}
      />
    </div>
  );
}

export default function CemeteryMap({ image, x, y, onChange, readOnly = false, label = true }) {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState("area");
  const [zoomAt, setZoomAt] = useState(null);
  const [justTappedZone, setJustTappedZone] = useState(false);
  const [localConfirmed, setLocalConfirmed] = useState(false);
  const hasPin = x != null && y != null;

  if (readOnly) {
    return (
      <div data-testid="cemetery-interactive-map" className="w-full overflow-hidden rounded-2xl border border-[#e2e8f0] bg-[#eef2f7] flex justify-center">
        <div className="relative">
          {image ? (
            <img src={image} alt="Vista aerea del cimitero" className="block max-h-80 max-w-full w-auto object-contain" draggable={false} />
          ) : (
            <div className="py-16 px-8 text-muted-soft text-sm">Immagine aerea non disponibile</div>
          )}
          {hasPin && <Pin x={x} y={y} confirmed />}
        </div>
      </div>
    );
  }

  const openModal = () => {
    if (localConfirmed && hasPin) {
      setZoomAt({ x, y });
      setPhase("precise");
    } else {
      setPhase("area");
      setZoomAt(null);
    }
    setJustTappedZone(false);
    setOpen(true);
  };

  const handleTap = (e) => {
    if (!onChange) return;
    const t = e.currentTarget;
    const nx = Math.min(1, Math.max(0, Number((e.nativeEvent.offsetX / t.clientWidth).toFixed(3))));
    const ny = Math.min(1, Math.max(0, Number((e.nativeEvent.offsetY / t.clientHeight).toFixed(3))));
    if (phase === "area") {
      setZoomAt({ x: nx, y: ny });
      setPhase("precise");
      setJustTappedZone(true);
      return;
    }
    setLocalConfirmed(false);
    onChange(nx, ny, false);
  };

  const confirm = () => {
    setLocalConfirmed(true);
    onChange(x, y, true);
    setOpen(false);
  };

  const altPoint = () => {
    setJustTappedZone(false);
    onChange(null, null, false);
  };

  const fullView = () => {
    setPhase("area");
    setZoomAt(null);
    setJustTappedZone(false);
    if (!localConfirmed) onChange(null, null, false);
  };

  return (
    <div data-testid="cemetery-interactive-map" className="space-y-3">
      <div
        role="button"
        tabIndex={0}
        onClick={openModal}
        data-testid="map-preview"
        className="relative w-full overflow-hidden rounded-2xl border border-[#e2e8f0] bg-[#eef2f7] flex justify-center cursor-pointer"
      >
        {image ? (
          <span className="relative inline-block">
            <img src={image} alt="Vista aerea completa del cimitero" className="block max-h-52 sm:max-h-64 max-w-full w-auto object-contain" draggable={false} />
            {hasPin && !open && <Pin x={x} y={y} confirmed={localConfirmed} />}
          </span>
        ) : (
          <span className="py-16 text-muted-soft text-sm">Immagine aerea non disponibile</span>
        )}
        <span className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-navy/90 text-white rounded-full px-4 py-1.5 text-xs font-medium whitespace-nowrap pointer-events-none">
          {localConfirmed && hasPin ? "Tocca per vedere o modificare la posizione" : hasPin ? "Posizione da confermare — tocca per continuare" : "Tocca per indicare la posizione del luogo"}
        </span>
      </div>

      {localConfirmed && hasPin ? (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm text-[#2f5d3e] font-medium flex items-center gap-1.5" data-testid="map-confirmed-text">
            <Check size={15} /> Posizione confermata
          </p>
          <button
            type="button"
            data-testid="map-modify-button"
            onClick={openModal}
            className="rounded-full border border-[#e2e8f0] bg-white px-4 py-2 text-sm text-navy hover:bg-slate-50 transition-colors"
          >
            Modifica posizione
          </button>
        </div>
      ) : (
        label && (
          <p className="text-xs text-muted-soft">
            Vedrai sempre l'intero cimitero. Tocca la zona, avvicineremo la vista, poi indicherai il punto preciso e lo confermerai.
          </p>
        )
      )}

      {open && (
        <div data-testid="map-modal" className="fixed inset-0 z-[80] flex flex-col bg-[#101c2e]">
          <div className="flex items-center justify-between px-4 py-3 shrink-0">
            <p className="text-white text-sm font-medium">
              {phase === "area" ? "Vista completa del cimitero" : "Scelta del punto preciso"}
            </p>
            <button type="button" data-testid="map-modal-close" onClick={() => setOpen(false)} className="text-white/80 hover:text-white p-2" aria-label="Chiudi mappa">
              <X size={22} />
            </button>
          </div>

          <div className="flex-1 min-h-0 relative overflow-hidden flex items-center justify-center px-2" data-testid="map-modal-canvas">
            <div
              className="relative transition-transform duration-700 ease-out"
              style={phase === "precise" && zoomAt ? { transform: `scale(${ZOOM})`, transformOrigin: `${zoomAt.x * 100}% ${zoomAt.y * 100}%` } : undefined}
            >
              {image ? (
                <img
                  src={image}
                  alt="Vista aerea del cimitero"
                  onClick={handleTap}
                  data-testid="map-modal-image"
                  className="block max-w-full w-auto h-auto max-h-[56vh] sm:max-h-[64vh] object-contain cursor-crosshair select-none touch-manipulation rounded-lg"
                  draggable={false}
                />
              ) : (
                <div className="py-16 px-8 text-white/60 text-sm">Immagine aerea non disponibile</div>
              )}
              {phase === "area" && (
                <div className="absolute top-2 left-2 bg-white/90 rounded-full px-3 py-1 text-xs font-semibold text-navy shadow-sm pointer-events-none">
                  Ingresso
                </div>
              )}
              {hasPin && <Pin x={x} y={y} confirmed={localConfirmed} />}
            </div>
          </div>

          <div className="shrink-0 w-full max-w-xl mx-auto px-4 pb-6 pt-3 space-y-3">
            {phase === "area" && (
              <p data-testid="map-zoom-hint" className="text-white/90 text-sm text-center leading-relaxed">
                Tocca la zona del cimitero in cui si trova il luogo: poi ci avvicineremo insieme.
              </p>
            )}
            {phase === "precise" && !hasPin && (
              <p data-testid="map-precise-hint" className="text-white/90 text-sm text-center leading-relaxed">
                {justTappedZone
                  ? "Perfetto. Ora avviciniamoci alla zona che hai indicato per scegliere il punto preciso. Tocca il punto esatto sulla mappa."
                  : "Tocca il punto preciso del luogo sulla mappa."}
              </p>
            )}
            {phase === "precise" && hasPin && (
              <div data-testid="map-check-message" className="space-y-3">
                <p className="text-white text-sm text-center leading-relaxed">
                  Hai indicato questo punto. Controlla che sia corretto: ci aiuterà a trovare facilmente il luogo durante il nostro intervento.
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button type="button" data-testid="map-confirm-button" onClick={confirm} className="btn-primary flex-1 !py-3">
                    Conferma posizione
                  </button>
                  <button
                    type="button"
                    data-testid="map-alt-point-button"
                    onClick={altPoint}
                    className="flex-1 rounded-full bg-white/10 border border-white/25 text-white px-4 py-3 text-sm hover:bg-white/20 transition-colors"
                  >
                    Scegli un altro punto
                  </button>
                </div>
              </div>
            )}
            <button
              type="button"
              data-testid="map-general-view-button"
              onClick={fullView}
              className="w-full text-center text-white/70 hover:text-white text-xs flex items-center justify-center gap-1.5 py-1 transition-colors"
            >
              <RotateCcw size={13} /> Torna alla vista completa
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
